import { isValidPhoneNumber, isPossiblePhoneNumber } from 'libphonenumber-js'
import type { DobRange } from './types'
import { todayCentral } from './dates'

// ---------------------------------------------------------------------------
// Client-side validation (§8). Matches the server's behavior so users rarely
// hit server errors. The server is authoritative; these mirror its rules.
// ---------------------------------------------------------------------------

/**
 * Name: first AND last. Server splits the full string and strips accents
 * (NFD normalize + remove combining marks). Practically: ≥ 2 words, each with
 * ≥ 2 letters once accents are stripped.
 */
export function validateName(raw: string): string | null {
  const value = (raw || '').trim()
  if (!value) return 'Please enter your full name.'
  const deaccented = value.normalize('NFD').replace(/[̀-ͯ]/g, '')
  const words = deaccented.split(/\s+/).filter((w) => (w.match(/[a-zA-Z]/g) || []).length >= 2)
  if (words.length < 2) return 'Please enter your first and last name.'
  return null
}

export { validateEmail } from './validateEmail'

/** US phone: both valid and possible per libphonenumber semantics (§8). */
/** True once a ZIP is a full five digits — the only form the API accepts (§2.2). */
export function isCompleteZip(zip: string): boolean {
  return /^\d{5}$/.test(zip)
}

export function validatePhone(raw: string): string | null {
  const value = (raw || '').trim()
  if (!value) return 'Please enter your cell phone number.'
  try {
    if (!isPossiblePhoneNumber(value, 'US') || !isValidPhoneNumber(value, 'US')) {
      return 'Please enter a valid US phone number.'
    }
  } catch {
    return 'Please enter a valid US phone number.'
  }
  return null
}

/**
 * Forgiving DOB parse: 5/9/24, 05/09/2024, 5-9-1990, 05.09.90, "5 9 1990",
 * bare digits (050924 / 05091990), and ISO YYYY-MM-DD.
 */
function parseDob(value: string): { y: number; m: number; d: number } | null {
  const iso = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(value)
  if (iso) return { y: +iso[1], m: +iso[2], d: +iso[3] }
  const us = /^(\d{1,2})[\s\-/.](\d{1,2})[\s\-/.](\d{2}|\d{4})$/.exec(value)
  if (us) return { m: +us[1], d: +us[2], y: expandYear(us[3]) }
  const digits = /^(\d{2})(\d{2})(\d{2}|\d{4})$/.exec(value) // MMDDYY / MMDDYYYY
  if (digits) return { m: +digits[1], d: +digits[2], y: expandYear(digits[3]) }
  return null
}

/** Two-digit DOB year → the century that isn't in the future ('24 → 2024, '32 → 1932). */
function expandYear(s: string): number {
  if (s.length === 4) return +s
  const [ty] = todayCentral().split('-').map(Number)
  const inThisCentury = Math.floor(ty / 100) * 100 + +s
  return inThisCentury <= ty ? inThisCentury : inThisCentury - 100
}

/** Real calendar date (rejects 02/30, month 13, etc.). */
function toCalendarDate({ y, m, d }: { y: number; m: number; d: number }): Date | null {
  const dt = new Date(Date.UTC(y, m - 1, d))
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null
  return dt
}

/** The canonical MM/DD/YYYY rendering of any parseable DOB input, else null. */
export function normalizeDob(raw: string): string | null {
  const parts = parseDob((raw || '').trim())
  if (!parts || !toCalendarDate(parts)) return null
  return `${String(parts.m).padStart(2, '0')}/${String(parts.d).padStart(2, '0')}/${parts.y}`
}

/**
 * DOB: forgiving input (see parseDob) → validity + age range.
 * Returns { error } or { error: null, ymd } where ymd is the API format.
 */
export function validateDob(raw: string, range: DobRange): { error: string | null; ymd?: string } {
  const value = (raw || '').trim()
  if (!value) return { error: 'Please enter your date of birth.' }

  const parts = parseDob(value)
  if (!parts) return { error: 'Enter a date like 5/9/1990.' }

  const dt = toCalendarDate(parts)
  if (!dt) return { error: 'That is not a valid date.' }

  const age = ageInYears(dt)
  if (age < range.min || age > range.max) {
    return { error: `Age must be between ${range.min} and ${range.max} years.` }
  }

  const { y, m, d } = parts
  const ymd = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  return { error: null, ymd }
}

function ageInYears(dob: Date): number {
  // Compare against "today" in Central (America/Chicago) — the practice timezone —
  // so age at day boundaries stays consistent with the rest of the date logic.
  const [ty, tm, td] = todayCentral().split('-').map(Number)
  const dm = dob.getUTCMonth() + 1
  const dd = dob.getUTCDate()
  let age = ty - dob.getUTCFullYear()
  if (tm < dm || (tm === dm && td < dd)) age--
  return age
}

/** Notes: required only when the profile has notes_required; ≥ ~2 alpha chars. */
export function validateNotes(raw: string, required: boolean): string | null {
  if (!required) return null
  const value = (raw || '').trim()
  const alpha = (value.match(/[a-zA-Z]/g) || []).length
  if (alpha < 2) return 'Please add a note for the provider.'
  return null
}
